import { useState, useCallback, useEffect } from 'react';
import { dasTicketService } from '../../api/services/dasTicketService';
import type {
  TakeActionType,
  ApprovalPosition,
  RelatedTicketDisplay,
  ApprovalLevelWithDynamic,
  DASTicketDetails,
  TakeActionRequest,
  WorkflowAction,
  TicketAttachment,
} from '../../api/types/dasTickets.types';
import type { User } from '../../api/types/auth.types';
import type { ID } from '../../api/types/common.types';

/**
 * Custom hook for Take Action functionality
 *
 * Handles fetching ticket data, approval position, related tickets,
 * and performing approval actions.
 *
 * NOTE: Currently uses mock data when API is not available
 */
export function useTakeAction(ticketId: ID | null) {
  // State
  const [ticket, setTicket] = useState<DASTicketDetails | null>(null);
  const [approvalPosition, setApprovalPosition] = useState<ApprovalPosition | null>(null);
  const [relatedTickets, setRelatedTickets] = useState<RelatedTicketDisplay[]>([]);
  const [approvalLevels, setApprovalLevels] = useState<ApprovalLevelWithDynamic[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Generate mock data for testing (temporary until backend is ready)
   */
  const generateMockData = useCallback((id: ID) => {
    const now = new Date();
    const twoDaysAgo = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000);

    // Check if this should be a completed ticket (even numbers show completed status)
    // Extract the last digit from the ID (handles formats like 'DAS-002', 'DAS-2024-002', or numeric IDs)
    const idString = String(id);
    const lastChar = idString.slice(-1);
    const isCompleted = /[0246]/.test(lastChar);

    // Mock attachments
    const mockAttachments: TicketAttachment[] = [
      {
        id: 'attachment-1',
        fileName: 'Purchase_Request_Details.pdf',
        documentTitle: 'Purchase Request Details',
        fileSize: 245678, // ~240 KB
        fileType: 'application/pdf',
        uploadedBy: 'John Smith',
        uploadedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        downloadUrl: '#', // Mock URL
      },
      {
        id: 'attachment-2',
        fileName: 'Equipment_Specifications.xlsx',
        documentTitle: 'Equipment Specifications',
        fileSize: 128934, // ~126 KB
        fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        uploadedBy: 'John Smith',
        uploadedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        downloadUrl: '#', // Mock URL
      },
      {
        id: 'attachment-3',
        fileName: 'Budget_Comparison.png',
        documentTitle: 'Budget Comparison',
        fileSize: 562341, // ~549 KB
        fileType: 'image/png',
        uploadedBy: 'John Smith',
        uploadedAt: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000).toISOString(),
        downloadUrl: '#', // Mock URL
      },
    ];

    // Mock ticket details
    const mockTicket: DASTicketDetails = {
      id: id,
      ticketNo: `TKT-${String(id).padStart(6, '0')}`,
      subject: 'Equipment Purchase Request - High Performance Laptops',
      description: 'Request for purchasing 10 high-performance laptops for the development team. Specifications include:\n- Intel i7 processor\n- 32GB RAM\n- 1TB SSD\n- Dedicated GPU\n\nEstimated delivery: 2 weeks from approval',
      requestType: 'Purchase Request',
      yourRole: isCompleted ? 'Approver' : 'Approver',
      currentStage: isCompleted ? 'Approved' : 'Level-2 Approval Pending',
      yourActionStatus: isCompleted ? 'Approved by You' : 'Pending – Your action needed',
      priority: 'High',
      status: isCompleted ? 'Archived' : 'Active',
      pendingSince: isCompleted ? new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString() : twoDaysAgo.toISOString(),
      pendingAge: isCompleted ? 0 : 2,
      ticketAge: isCompleted ? 10 : 5,
      createdAt: new Date(now.getTime() - (isCompleted ? 10 : 5) * 24 * 60 * 60 * 1000).toISOString(),
      createdBy: 'user-123',
      createdByUserId: 'user-123',
      createdByUserName: 'John Smith',
      lastActionBy: isCompleted ? 'David Brown' : undefined,
      lastActionDate: isCompleted ? new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString() : undefined,
      actionCompletedByYouOn: isCompleted ? new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000).toISOString() : undefined,
      value: 25000.00, // 10 laptops × $2,500 each
      attachments: mockAttachments,
      workflowHistory: [],
      approvalHierarchy: [],
      relatedTickets: [],
    };

    // Mock approval position
    const mockPosition: ApprovalPosition = {
      level: isCompleted ? 4 : 2,
      levelNumber: isCompleted ? 4 : 2,
      levelName: isCompleted ? 'VP Approval' : 'Department Head Approval',
      isCurrentLevel: !isCompleted,
      hasActioned: isCompleted,
      actionTaken: isCompleted ? 'approve' : undefined,
      actionDate: isCompleted ? new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000).toISOString() : undefined,
      actionComments: isCompleted ? 'Approved - justified purchase for team productivity enhancement.' : undefined,
      canTakeAction: !isCompleted,
      isFinalApprover: false,
      isDynamicLevel: false,
    };

    // Mock related tickets
    const mockRelated: RelatedTicketDisplay[] = [
      {
        ticketId: 'related-1',
        ticketNo: 'TKT-000123',
        subject: 'Office Space Expansion',
        status: 'Approved',
        relationshipType: 'Related',
        createdDate: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        priority: 'Normal',
      },
    ];

    // Mock role objects
    const mockManagerRole = {
      id: 'role-manager',
      code: 'MANAGER',
      name: 'Manager',
      roleType: 'functional',
      assignmentType: 'direct',
    };

    const mockAdminRole = {
      id: 'role-admin',
      code: 'ADMIN',
      name: 'Admin',
      roleType: 'system',
      assignmentType: 'direct',
    };

    // Mock available users for forwarding and adding approvers
    const mockUsers: User[] = [
      {
        id: 'user-101',
        username: 'sarah.johnson',
        email: 'sarah.johnson@company.com',
        firstName: 'Sarah',
        lastName: 'Johnson',
        fullName: 'Sarah Johnson',
        roles: [mockManagerRole],
        role: 'Manager',
        permissions: [],
        entityIds: [],
        isActive: true,
        createdAt: new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'user-102',
        username: 'mike.chen',
        email: 'mike.chen@company.com',
        firstName: 'Mike',
        lastName: 'Chen',
        fullName: 'Mike Chen',
        roles: [mockAdminRole],
        role: 'Admin',
        permissions: [],
        entityIds: [],
        isActive: true,
        createdAt: new Date(now.getTime() - 200 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'user-103',
        username: 'emma.wilson',
        email: 'emma.wilson@company.com',
        firstName: 'Emma',
        lastName: 'Wilson',
        fullName: 'Emma Wilson',
        roles: [mockManagerRole],
        role: 'Manager',
        permissions: [],
        entityIds: [],
        isActive: true,
        createdAt: new Date(now.getTime() - 150 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'user-104',
        username: 'david.brown',
        email: 'david.brown@company.com',
        firstName: 'David',
        lastName: 'Brown',
        fullName: 'David Brown',
        roles: [mockAdminRole],
        role: 'Admin',
        permissions: [],
        entityIds: [],
        isActive: true,
        createdAt: new Date(now.getTime() - 300 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 'user-105',
        username: 'lisa.martinez',
        email: 'lisa.martinez@company.com',
        firstName: 'Lisa',
        lastName: 'Martinez',
        fullName: 'Lisa Martinez',
        roles: [mockManagerRole],
        role: 'Manager',
        permissions: [],
        entityIds: [],
        isActive: true,
        createdAt: new Date(now.getTime() - 250 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ];

    // Mock approval levels with dynamic insertion example
    const mockLevels: ApprovalLevelWithDynamic[] = [
      {
        level: 1,
        levelName: 'Team Lead Review',
        approverName: 'Sarah Johnson',
        approverRole: 'Team Lead',
        status: 'Approved',
        actionDate: new Date(now.getTime() - (isCompleted ? 9 : 4) * 24 * 60 * 60 * 1000).toISOString(),
        comments: 'Team capacity confirmed. These laptops are essential for the new project requirements.',
        isDynamicallyInserted: false,
      },
      {
        level: 2,
        levelName: 'Department Head Approval',
        approverName: 'Current User',
        approverRole: 'Department Head',
        status: isCompleted ? 'Approved' : 'Pending',
        actionDate: isCompleted ? new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000).toISOString() : undefined,
        comments: isCompleted ? 'Approved - justified purchase for team productivity enhancement.' : undefined,
        isDynamicallyInserted: false,
      },
      {
        level: 3,
        levelName: 'Finance Review',
        approverName: 'Mike Chen',
        approverRole: 'Finance Manager',
        status: isCompleted ? 'Approved' : 'Pending',
        actionDate: isCompleted ? new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString() : undefined,
        comments: isCompleted ? 'Budget allocation approved. Funds are available from Q4 capital budget.' : undefined,
        isDynamicallyInserted: true,
        insertedByName: 'Department Head',
        insertionReason: 'High-value purchase requires additional financial review',
      },
      {
        level: 4,
        levelName: 'VP Approval',
        approverName: 'David Brown',
        approverRole: 'VP Operations',
        status: isCompleted ? 'Approved' : 'Pending',
        actionDate: isCompleted ? new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString() : undefined,
        comments: isCompleted ? 'Final approval granted. Strategic investment in team capabilities.' : undefined,
        isDynamicallyInserted: false,
      },
    ];

    // Mock workflow history
    const mockHistory: WorkflowAction[] = isCompleted
      ? [
          {
            id: 'action-1',
            action: 'Created',
            actionBy: 'John Smith',
            actionByRole: 'Developer',
            actionDate: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
            comments: 'Urgent requirement for the development team to meet Q4 project deadlines.',
            level: 0,
          },
          {
            id: 'action-2',
            action: 'Approved',
            actionBy: 'Sarah Johnson',
            actionByRole: 'Team Lead',
            actionDate: new Date(now.getTime() - 9 * 24 * 60 * 60 * 1000).toISOString(),
            comments: 'Team capacity confirmed. These laptops are essential for the new project requirements.',
            level: 1,
          },
          {
            id: 'action-3',
            action: 'Approved',
            actionBy: 'Current User',
            actionByRole: 'Department Head',
            actionDate: new Date(now.getTime() - 8 * 24 * 60 * 60 * 1000).toISOString(),
            comments: 'Approved - justified purchase for team productivity enhancement.',
            level: 2,
          },
          {
            id: 'action-4',
            action: 'Approved',
            actionBy: 'Mike Chen',
            actionByRole: 'Finance Manager',
            actionDate: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000).toISOString(),
            comments: 'Budget allocation approved. Funds are available from Q4 capital budget.',
            level: 3,
          },
          {
            id: 'action-5',
            action: 'Approved',
            actionBy: 'David Brown',
            actionByRole: 'VP Operations',
            actionDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
            comments: 'Final approval granted. Strategic investment in team capabilities.',
            level: 4,
          },
        ]
      : [
          {
            id: 'action-1',
            action: 'Created',
            actionBy: 'John Smith',
            actionByRole: 'Developer',
            actionDate: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
            comments: 'Urgent requirement for the development team to meet Q4 project deadlines.',
            level: 0,
          },
          {
            id: 'action-2',
            action: 'Approved',
            actionBy: 'Sarah Johnson',
            actionByRole: 'Team Lead',
            actionDate: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000).toISOString(),
            comments: 'Team capacity confirmed. These laptops are essential for the new project requirements.',
            level: 1,
          },
        ];

    return { mockTicket, mockPosition, mockRelated, mockUsers, mockLevels, mockHistory };
  }, []);

  /**
   * Fetch ticket data
   */
  const fetchTicketData = useCallback(async () => {
    if (!ticketId) return;

    setIsLoading(true);
    setError(null);

    try {
      // CRITICAL: Fetch ticket details (this is required)
      const ticketResponse = await dasTicketService.getDetails(ticketId);
      if (ticketResponse.success && ticketResponse.data) {
        setTicket(ticketResponse.data);
      } else {
        throw new Error(ticketResponse.message || 'Failed to fetch ticket details');
      }

      // OPTIONAL: Set default approval position
      // Note: Backend endpoint /tickets/{id}/approval-position not implemented yet
      // IMPORTANT: When a request is returned for clarification, only the creator can take action
      // Pre-approvers and other approvers should NOT be able to take action on returned requests
      const isReturned = ticketResponse.data.currentStage === 'Returned';
      const isCreator = ticketResponse.data.yourRole === 'Creator';
      const canUserTakeAction = !isReturned || isCreator;

      setApprovalPosition({
        level: 1,
        levelNumber: 1,
        levelName: 'Level 1 Approval',
        isCurrentLevel: true,
        hasActioned: false,
        canTakeAction: canUserTakeAction,
        isFinalApprover: false,
        isDynamicLevel: false,
      });

      // OPTIONAL: Set empty related tickets
      // Note: Backend endpoint /tickets/{id}/related-tickets not implemented yet
      setRelatedTickets([]);

      // OPTIONAL: Set empty approval levels
      // Note: Backend endpoint /tickets/{id}/approval-levels-with-dynamic not implemented yet
      setApprovalLevels([]);
    } catch (err) {
      const error = err as Error;
      console.error('❌ Failed to load ticket details:', {
        ticketId,
        error: error.message,
        stack: error.stack,
      });

      setError(error.message || 'Failed to load ticket details');
      setTicket(null);
    } finally {
      setIsLoading(false);
    }
  }, [ticketId, generateMockData]);

  /**
   * Take action on the ticket
   */
  const takeAction = useCallback(
    async (request: TakeActionRequest): Promise<void> => {
      if (!ticketId) {
        throw new Error('No ticket ID provided');
      }

      const response = await dasTicketService.takeAction(request);

      if (!response.success) {
        throw new Error(response.message || 'Failed to submit action');
      }

      // Refresh ticket data after successful action
      await fetchTicketData();
    },
    [ticketId, fetchTicketData]
  );

  /**
   * Fetch data when ticketId changes
   */
  useEffect(() => {
    if (ticketId) {
      fetchTicketData();
    }
  }, [ticketId, fetchTicketData]);

  return {
    ticket,
    approvalPosition,
    relatedTickets,
    approvalLevels,
    availableUsers,
    isLoading,
    error,
    takeAction,
    refresh: fetchTicketData,
  };
}

export default useTakeAction;
